/**
 * Lackey — dev-only remote eval bridge. Wire with tools/lackey server (see tools/lackey/cli.js).
 *
 * Enable by setting `VUE_APP_LACKEY_PORT` (e.g. in `.env.development.local`) to a positive port
 * matching `lackey start` / `LACKEY_PORT`. If unset, empty, or 0, Lackey is inert (no WebSocket, no work).
 *
 * Example (main.js):
 *   import lackey from '@/dev/lackey';
 *   lackey.connect();
 *   lackey.channel('app', () => window);
 *
 * Example (inside a component mounted()):
 *   lackey.channel('lesson-editor', () => this);
 */

/**
 * @returns {number|null} Parsed port, or null when Lackey should stay off.
 */
function parseVueAppLackeyPort() {
  const raw = process.env.VUE_APP_LACKEY_PORT;
  if (raw === undefined || raw === null) {
    return null;
  }
  const s = String(raw).trim();
  if (s === '') {
    return null;
  }
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n <= 0) {
    return null;
  }
  return n;
}

const configuredPort = parseVueAppLackeyPort();
const lackeyEnabled = configuredPort !== null;

function noop() {}

/** @type {Map<string, () => unknown | unknown>} */
const contexts = new Map();

let wsRef = null;

function wsUrl(opts) {
  if (opts.url) {
    return opts.url;
  }
  const host = opts.host || window.location.hostname;
  const port = opts.port != null ? opts.port : configuredPort;
  const proto = opts.secure ? 'wss' : 'ws';
  return `${proto}://${host}:${port}`;
}

function send(ws, obj) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(obj));
  }
}

function serializeForWire(value) {
  const seen = new WeakSet();
  try {
    return JSON.parse(
      JSON.stringify(value, (key, val) => {
        if (typeof val === 'function') {
          return `[Function ${val.name || 'anonymous'}]`;
        }
        if (typeof val === 'bigint') {
          return val.toString();
        }
        if (val && typeof val === 'object') {
          if (seen.has(val)) {
            return '[Circular]';
          }
          seen.add(val);
        }
        return val;
      })
    );
  } catch {
    return { __lackeyError: 'not JSON-serializable', string: String(value) };
  }
}

function resolveContext(channel) {
  const entry = contexts.get(channel);
  if (entry === undefined) {
    return undefined;
  }
  return typeof entry === 'function' ? entry() : entry;
}

function runEvalInContext(code, ctx) {
  try {
    const fn = new Function(
      'ctx',
      'return (function () {\n' + code + '\n}).call(ctx);'
    );
    const raw = fn(ctx);
    return { ok: true, result: serializeForWire(raw) };
  } catch (e) {
    return { ok: false, error: String((e && e.message) || e) };
  }
}

function attachHandlers(ws) {
  ws.addEventListener('message', (ev) => {
    let msg;
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if (msg.role !== 'server' || msg.type !== 'eval') {
      return;
    }
    const channel = String(msg.channel || '');
    const id = String(msg.id || '');
    const code = String(msg.code || '');
    const ctx = resolveContext(channel);
    if (ctx === undefined) {
      send(ws, {
        role: 'browser',
        type: 'eval_result',
        channel,
        id,
        ok: false,
        error: `no context registered for channel "${channel}"`,
      });
      return;
    }
    const out = runEvalInContext(code, ctx);
    send(ws, {
      role: 'browser',
      type: 'eval_result',
      channel,
      id,
      ok: out.ok,
      result: out.ok ? out.result : undefined,
      error: out.ok ? undefined : out.error,
    });
  });
}

function sendRegister(ws, channel) {
  send(ws, {
    role: 'browser',
    type: 'register',
    channel,
  });
}

function registerAllChannels(ws) {
  contexts.forEach((_v, channel) => {
    sendRegister(ws, channel);
  });
}

const lackeyActive = {
  enabled: true,

  /**
   * @param {{ url?: string, host?: string, port?: number, secure?: boolean }} opts
   */
  connect(opts = {}) {
    if (wsRef && wsRef.readyState === WebSocket.OPEN) {
      wsRef.close();
    }
    const url = wsUrl(opts);
    const ws = new WebSocket(url);
    wsRef = ws;
    attachHandlers(ws);
    ws.addEventListener('open', () => {
      registerAllChannels(ws);
    });
    ws.addEventListener('close', () => {
      if (wsRef === ws) {
        wsRef = null;
      }
    });
    return ws;
  },

  /**
   * Bind a channel name to a `this` / scope for eval. Pass `window` or `() => this` from a component.
   * @param {string} channel
   * @param {unknown | (() => unknown)} contextOrGetter
   */
  channel(channel, contextOrGetter) {
    contexts.set(channel, contextOrGetter);
    if (wsRef && wsRef.readyState === WebSocket.OPEN) {
      sendRegister(wsRef, channel);
    }
  },

  on(channel, contextOrGetter) {
    return this.channel(channel, contextOrGetter);
  },

  unregister(channel) {
    contexts.delete(channel);
    if (wsRef && wsRef.readyState === WebSocket.OPEN) {
      send(wsRef, {
        role: 'browser',
        type: 'unregister',
        channel,
      });
    }
  },
};

const lackeyInactive = {
  enabled: false,
  connect: noop,
  channel: noop,
  on: noop,
  unregister: noop,
};

const lackey = lackeyEnabled ? lackeyActive : lackeyInactive;

export default lackey;
